import { VoxelAsset, VoxelData } from '../../voxelTypes';
import { PALETTE } from '../../voxelColor';
import { addMiniBox } from './CatalogUtils';

export type CatStyle = 'tabby' | 'tuxedo' | 'ginger' | 'cream';
export type DogStyle = 'brown' | 'black' | 'cream' | 'spotted';
export type RabbitStyle = 'gray' | 'white' | 'brown' | 'black';
export type ChickenStyle = 'red' | 'white' | 'speckled' | 'black';
export type TurtleStyle = 'moss' | 'olive' | 'amber' | 'speckled';
export type SheepStyle = 'white' | 'suffolk' | 'caramel' | 'pink';

export function getPetCatalog(): VoxelAsset[] {
  const assets: VoxelAsset[] = [];

  // --- 1. CATS ---
  const catStyles: { id: CatStyle; label: string; pCol: number; sCol: number }[] = [
    { id: 'tabby', label: 'Tabby Cat', pCol: 0x8a776c, sCol: 0x544740 },
    { id: 'tuxedo', label: 'Tuxedo Cat', pCol: 0x1a1a1a, sCol: 0xfafafa },
    { id: 'ginger', label: 'Ginger Cat', pCol: 0xd97e36, sCol: 0xe89f58 },
    { id: 'cream', label: 'Cream Cat', pCol: 0xf5ebd5, sCol: 0xffffff }
  ];

  catStyles.forEach(style => {
    const voxels: VoxelData[] = [];
    const p = style.pCol;
    const s = style.sCol;

    // Body (solid base, centered on x: -1.0 to 0.5)
    addMiniBox(voxels, -1.0, 0.5, -1.5, 4, 3, 6, p);
    // Legs (connect cleanly to y: 0.5)
    addMiniBox(voxels, -1.0, 0.0, 0.5, 1, 1, 1, p); // Front-Left
    addMiniBox(voxels, 0.5, 0.0, 0.5, 1, 1, 1, p);  // Front-Right
    addMiniBox(voxels, -1.0, 0.0, -1.0, 1, 1, 1, p); // Back-Left
    addMiniBox(voxels, 0.5, 0.0, -1.0, 1, 1, 1, p);  // Back-Right

    // Tuxedo / Special Chest Bib (solidly overlays body front)
    addMiniBox(voxels, -0.5, 0.5, 1.0, 2, 3, 1, s);

    // Head (directly on top of body at y: 2.0 to 4.0)
    addMiniBox(voxels, -1.0, 2.0, 0.0, 4, 4, 4, p);

    // Pointed Upright Ears (on top of head at y: 4.0)
    addMiniBox(voxels, -1.0, 4.0, 0.5, 1, 2, 1, p); // Left ear
    addMiniBox(voxels, 0.5, 4.0, 0.5, 1, 2, 1, p);  // Right ear

    // Snout / Muzzle and Pink Nose
    addMiniBox(voxels, -0.5, 2.0, 1.5, 2, 2, 1, s);
    // Tiny pink nose centered
    voxels.push({ x: -0.25, y: 2.5, z: 2.0, color: 0xffa3b1, scale: 0.5 });

    // Cute Square Eyes
    voxels.push({ x: -0.75, y: 3.0, z: 1.5, color: 0x111111, scale: 0.5 });
    voxels.push({ x: 0.25, y: 3.0, z: 1.5, color: 0x111111, scale: 0.5 });

    // Curly tail at back
    addMiniBox(voxels, -0.25, 0.5, -2.0, 1, 3, 1, p); // base vertical
    addMiniBox(voxels, -0.25, 1.5, -2.5, 1, 1, 2, p); // tail curve

    assets.push({
      id: `pet_cat_${style.id}`,
      name: `Cat - ${style.label}`,
      category: 'prop',
      description: `An adorable miniature sitting pet cat with upright pointed ears and a curled tail. Style: ${style.label}.`,
      voxels,
      dimensions: { x: 3, y: 3.5, z: 4.5 }
    });
  });

  // --- 2. DOGS ---
  const dogStyles: { id: DogStyle; label: string; pCol: number; sCol: number }[] = [
    { id: 'brown', label: 'Brown Retriever', pCol: 0x6e4a31, sCol: 0x473020 },
    { id: 'black', label: 'Black Lab', pCol: 0x1c1c1c, sCol: 0x111111 },
    { id: 'cream', label: 'Golden Cream', pCol: 0xf5dfbc, sCol: 0xfff3db },
    { id: 'spotted', label: 'Spotted Dalmatian', pCol: 0xfcfcfc, sCol: 0x1a1a1a }
  ];

  dogStyles.forEach(style => {
    const voxels: VoxelData[] = [];
    const p = style.pCol;
    const s = style.sCol;

    // Robust Body (solidly centered)
    addMiniBox(voxels, -1.25, 0.5, -1.75, 5, 4, 7, p);

    // Legs (fully touch ground at y:0 and connect to body at y:0.5)
    addMiniBox(voxels, -1.25, 0.0, 0.75, 1, 1, 1, p);  // Front-Left
    addMiniBox(voxels, 0.25, 0.0, 0.75, 1, 1, 1, p);   // Front-Right
    addMiniBox(voxels, -1.25, 0.0, -1.25, 1, 1, 1, p); // Back-Left
    addMiniBox(voxels, 0.25, 0.0, -1.25, 1, 1, 1, p);  // Back-Right

    // Dalmatian Spots (completely connected flat overlaps on the skin)
    if (style.id === 'spotted') {
      addMiniBox(voxels, -1.5, 1.0, -0.25, 1, 2, 2, s); // Left side spot
      addMiniBox(voxels, 0.75, 1.5, 0.25, 1, 2, 2, s);  // Right side spot
      addMiniBox(voxels, -0.25, 2.5, -1.25, 2, 1, 2, s); // Back spot
    }

    // Head (directly connects to body at y: 2.5)
    addMiniBox(voxels, -1.25, 2.5, 0.25, 5, 4, 4, p);

    // Muzzle / Snout and Nose Tip
    addMiniBox(voxels, -0.75, 2.5, 1.75, 3, 2, 2, s);
    addMiniBox(voxels, -0.25, 3.0, 2.75, 1, 1, 1, 0x111111); // nose tip

    // Floppy ears on the sides (connect directly to the head at x: -1.25 and 0.75)
    addMiniBox(voxels, -1.75, 2.0, 0.5, 1, 4, 2, s); // Left floppy ear
    addMiniBox(voxels, 0.75, 2.0, 0.5, 1, 4, 2, s);  // Right floppy ear

    // Square Eyes on Face front
    voxels.push({ x: -0.75, y: 3.5, z: 1.75, color: 0x111111, scale: 0.5 });
    voxels.push({ x: 0.25, y: 3.5, z: 1.75, color: 0x111111, scale: 0.5 });

    // Happy Tail (connected and wagging back and up)
    addMiniBox(voxels, -0.25, 2.0, -2.75, 1, 2, 2, p);

    assets.push({
      id: `pet_dog_${style.id}`,
      name: `Dog - ${style.label}`,
      category: 'prop',
      description: `A sweet garden pup pet with floppy ears, a snuffling muzzle, and a wagging tail. Style: ${style.label}.`,
      voxels,
      dimensions: { x: 3.5, y: 4, z: 5.5 }
    });
  });

  // --- 3. RABBITS ---
  const rabbitStyles: { id: RabbitStyle; label: string; pCol: number }[] = [
    { id: 'gray', label: 'Cottontail Gray', pCol: 0x9ca3af },
    { id: 'white', label: 'Angora White', pCol: 0xffffff },
    { id: 'brown', label: 'Woodland Brown', pCol: 0x8c6239 },
    { id: 'black', label: 'Velvet Black', pCol: 0x222222 }
  ];

  rabbitStyles.forEach(style => {
    const voxels: VoxelData[] = [];
    const p = style.pCol;

    // Plump bunny sitting body (solidly connected)
    addMiniBox(voxels, -1.0, 0.5, -1.5, 4, 3, 5, p);

    // Large fluffy rear thighs
    addMiniBox(voxels, -1.5, 0.5, -1.0, 1, 2, 2, p); // Left thigh
    addMiniBox(voxels, 0.5, 0.5, -1.0, 1, 2, 2, p);  // Right thigh

    // Tiny paws touching ground
    addMiniBox(voxels, -1.0, 0.0, 0.5, 1, 1, 1, p);  // Front-Left
    addMiniBox(voxels, 0.0, 0.0, 0.5, 1, 1, 1, p);   // Front-Right
    addMiniBox(voxels, -1.5, 0.0, -1.0, 1, 1, 2, p); // Back-Left
    addMiniBox(voxels, 0.5, 0.0, -1.0, 1, 1, 2, p);  // Back-Right

    // Head (directly connects to body at y: 2.0)
    addMiniBox(voxels, -1.0, 2.0, -0.25, 4, 4, 4, p);

    // Long upright ears with lovely pink inner liners
    addMiniBox(voxels, -1.0, 4.0, 0.5, 1, 5, 1, p); // Left ear
    addMiniBox(voxels, -1.0, 4.5, 1.0, 1, 4, 1, 0xffb8c6); // Inner Left pink
    addMiniBox(voxels, 0.5, 4.0, 0.5, 1, 5, 1, p);  // Right ear
    addMiniBox(voxels, 0.5, 4.5, 1.0, 1, 4, 1, 0xffb8c6);  // Inner Right pink

    // Cute twitchy nose
    voxels.push({ x: -0.25, y: 2.5, z: 1.25, color: 0xffb8c6, scale: 0.5 });
    voxels.push({ x: -0.75, y: 3.0, z: 1.25, color: 0x111111, scale: 0.5 }); // Left eye
    voxels.push({ x: 0.25, y: 3.0, z: 1.25, color: 0x111111, scale: 0.5 });  // Right eye

    // Puffy white tail at back
    addMiniBox(voxels, -0.5, 1.0, -2.0, 2, 2, 1, 0xffffff);

    assets.push({
      id: `pet_rabbit_${style.id}`,
      name: `Rabbit - ${style.label}`,
      category: 'prop',
      description: `A fluffy sitting rabbit featuring extremely long upright ears with pink liners and a round cottontail. Style: ${style.label}.`,
      voxels,
      dimensions: { x: 2.5, y: 5, z: 3.5 }
    });
  });

  // --- 4. CHICKENS ---
  const chickenStyles: { id: ChickenStyle; label: string; pCol: number }[] = [
    { id: 'red', label: 'Rhode Island Red', pCol: 0xa64333 },
    { id: 'white', label: 'Leghorn White', pCol: 0xffffff },
    { id: 'speckled', label: 'Speckled Plymouth', pCol: 0x9c9c9c },
    { id: 'black', label: 'Australorp Black', pCol: 0x1a1a1a }
  ];

  chickenStyles.forEach(style => {
    const voxels: VoxelData[] = [];
    const p = style.pCol;

    // Plump feather body
    addMiniBox(voxels, -1.0, 1.0, -1.5, 4, 3, 5, p);

    // Stick legs (perfectly contiguous from y:0 to y:1.0)
    addMiniBox(voxels, -0.5, 0.0, -0.5, 1, 2, 1, PALETTE.FLOWER_YELLOW);
    addMiniBox(voxels, 0.0, 0.0, -0.5, 1, 2, 1, PALETTE.FLOWER_YELLOW);

    // Head and neck (connects to body top at y: 2.5)
    addMiniBox(voxels, -0.75, 2.5, 0.25, 3, 4, 3, p);

    // Beak
    addMiniBox(voxels, -0.25, 3.5, 1.75, 1, 1, 2, PALETTE.FLOWER_YELLOW);

    // Red Crown Comb
    addMiniBox(voxels, -0.25, 4.5, 0.25, 1, 2, 3, PALETTE.FLOWER_RED);

    // Wattles under beak
    addMiniBox(voxels, -0.25, 2.75, 1.25, 1, 2, 1, PALETTE.FLOWER_RED);

    // Wings on the sides
    addMiniBox(voxels, -1.5, 1.5, -1.0, 1, 2, 4, p); // Left wing
    addMiniBox(voxels, 1.0, 1.5, -1.0, 1, 2, 4, p);  // Right wing

    // Tail feathers pointing up
    addMiniBox(voxels, -0.75, 2.0, -2.5, 3, 3, 2, p);

    assets.push({
      id: `pet_chicken_${style.id}`,
      name: `Chicken - ${style.label}`,
      category: 'prop',
      description: `A plump garden laying hen with yellow legs, wing folds, and a proud crimson crown. Style: ${style.label}.`,
      voxels,
      dimensions: { x: 2.5, y: 3.5, z: 3.5 }
    });
  });

  // --- 5. TURTLES ---
  const turtleStyles: { id: TurtleStyle; label: string; shellCol: number; skinCol: number }[] = [
    { id: 'moss', label: 'Moss Turtle', shellCol: 0x3d6624, skinCol: 0x6bb33e },
    { id: 'olive', label: 'Olive Turtle', shellCol: 0x545c38, skinCol: 0x8f9c5c },
    { id: 'amber', label: 'Amber Terrapin', shellCol: 0xd9832b, skinCol: 0xd9c28b },
    { id: 'speckled', label: 'Speckled Box', shellCol: 0x4f3621, skinCol: 0x9e7a46 }
  ];

  turtleStyles.forEach(style => {
    const voxels: VoxelData[] = [];
    const s = style.shellCol;
    const k = style.skinCol;

    // Skin base
    addMiniBox(voxels, -1.5, 0.25, -1.5, 6, 1, 6, k);

    // Flipper legs (fully connected at y:0 and x: -2.0 & 1.0)
    addMiniBox(voxels, -2.0, 0.0, 1.0, 1, 1, 1, k);  // Front-Left
    addMiniBox(voxels, 1.0, 0.0, 1.0, 1, 1, 1, k);   // Front-Right
    addMiniBox(voxels, -2.0, 0.0, -2.0, 1, 1, 1, k); // Back-Left
    addMiniBox(voxels, 1.0, 0.0, -2.0, 1, 1, 1, k);  // Back-Right

    // Shell dome
    addMiniBox(voxels, -1.5, 0.75, -1.5, 6, 3, 6, s);
    addMiniBox(voxels, -1.0, 2.25, -1.0, 4, 1, 4, s); // top cap

    if (style.id === 'speckled') {
      voxels.push({ x: -0.5, y: 2.5, z: 0.0, color: PALETTE.FLOWER_YELLOW, scale: 0.5 });
      voxels.push({ x: 0.5, y: 2.0, z: -0.5, color: PALETTE.FLOWER_YELLOW, scale: 0.5 });
    } else if (style.id === 'moss') {
      addMiniBox(voxels, -1.0, 2.5, -1.0, 4, 1, 4, PALETTE.MOSS, 'foliage');
    }

    // Stately head and neck stretching forward
    addMiniBox(voxels, -0.5, 0.5, 1.5, 2, 2, 3, k);
    // Tiny black eyes
    voxels.push({ x: -0.75, y: 1.5, z: 2.5, color: 0x111111, scale: 0.5 });
    voxels.push({ x: 0.25, y: 1.5, z: 2.5, color: 0x111111, scale: 0.5 });

    assets.push({
      id: `pet_turtle_${style.id}`,
      name: `Turtle - ${style.label}`,
      category: 'prop',
      description: `A quiet slow-walking garden turtle featuring crawling webbed legs and a protective honeycomb dome shell. Style: ${style.label}.`,
      voxels,
      dimensions: { x: 3, y: 2, z: 4 }
    });
  });

  // --- 6. FLUFFY SHEEP ---
  const sheepStyles: {
    id: SheepStyle;
    label: string;
    woolCol: number;
    woolHighlight: number;
    woolShadow: number;
    faceCol: number;
    legCol: number;
  }[] = [
    {
      id: 'white',
      label: 'Fluffy White Cloud',
      woolCol: 0xffffff,
      woolHighlight: 0xfff9f2,
      woolShadow: 0xded8cc,
      faceCol: 0x3d3835,
      legCol: 0x262220,
    },
    {
      id: 'suffolk',
      label: 'Suffolk Blackface',
      woolCol: 0xf7ebd5,
      woolHighlight: 0xfff8ea,
      woolShadow: 0xded1bb,
      faceCol: 0x1a1a1a,
      legCol: 0x141414,
    },
    {
      id: 'caramel',
      label: 'Golden Caramel',
      woolCol: 0xd9a86c,
      woolHighlight: 0xe8be8b,
      woolShadow: 0xb8884d,
      faceCol: 0x4a3222,
      legCol: 0x362316,
    },
    {
      id: 'pink',
      label: 'Cotton Candy Pink',
      woolCol: 0xffb6c1,
      woolHighlight: 0xffd1dc,
      woolShadow: 0xe699a8,
      faceCol: 0xf5d3c4,
      legCol: 0xd4a596,
    },
  ];

  sheepStyles.forEach(style => {
    const voxels: VoxelData[] = [];
    const { woolCol, woolHighlight, woolShadow, faceCol, legCol } = style;

    // 1. Trotter Legs (4 connected legs touching y:0)
    addMiniBox(voxels, -1.25, 0.0, 1.0, 2, 2, 2, legCol);  // Front-Left
    addMiniBox(voxels, 0.25, 0.0, 1.0, 2, 2, 2, legCol);   // Front-Right
    addMiniBox(voxels, -1.25, 0.0, -1.5, 2, 2, 2, legCol); // Back-Left
    addMiniBox(voxels, 0.25, 0.0, -1.5, 2, 2, 2, legCol);  // Back-Right

    // Dark hooves at bottom
    addMiniBox(voxels, -1.25, 0.0, 1.25, 2, 1, 1, 0x111111);
    addMiniBox(voxels, 0.25, 0.0, 1.25, 2, 1, 1, 0x111111);
    addMiniBox(voxels, -1.25, 0.0, -1.25, 2, 1, 1, 0x111111);
    addMiniBox(voxels, 0.25, 0.0, -1.25, 2, 1, 1, 0x111111);

    // 2. Underbelly shadow layer
    addMiniBox(voxels, -1.25, 0.75, -1.5, 5, 1, 7, woolShadow);

    // 3. Main Fluffy Wool Fleece (Core cloud torso)
    addMiniBox(voxels, -1.75, 1.0, -2.0, 7, 5, 8, woolCol);

    // 4. Volumetric Wool Tufts & Puffs (Bulging cloud-like texture)
    // Left & Right side bulging puffs
    addMiniBox(voxels, -2.25, 1.5, -1.5, 1, 4, 6, woolHighlight);
    addMiniBox(voxels, 1.75, 1.5, -1.5, 1, 4, 6, woolHighlight);
    // Back ridge & top cloud bumps
    addMiniBox(voxels, -1.25, 3.5, -1.5, 5, 1, 7, woolHighlight);
    addMiniBox(voxels, -0.75, 4.0, -1.0, 3, 1, 3, woolHighlight);
    addMiniBox(voxels, -0.75, 4.0, 0.5, 3, 1, 2, woolCol);
    // Back fluffy tail puff
    addMiniBox(voxels, -0.75, 1.5, -2.5, 3, 3, 1, woolHighlight);
    addMiniBox(voxels, -0.25, 2.0, -3.0, 1, 2, 1, woolCol);

    // 5. Fluffy Neck / Woolly Hood Collar
    addMiniBox(voxels, -1.75, 1.5, 1.5, 7, 4, 2, woolCol);
    addMiniBox(voxels, -1.25, 3.5, 1.5, 5, 1, 2, woolHighlight);

    // 6. Head & Face (sticking out from fleece)
    addMiniBox(voxels, -1.25, 1.75, 2.0, 5, 3, 3, faceCol);
    addMiniBox(voxels, -0.75, 1.75, 3.5, 3, 2, 1, faceCol); // Muzzle/Snout
    addMiniBox(voxels, -0.25, 2.25, 4.0, 1, 1, 1, 0x111111); // Black Nose Tip

    // Cute Eyes with highlights
    voxels.push({ x: -0.75, y: 2.75, z: 3.5, color: 0x111111, scale: 0.5 });
    voxels.push({ x: 0.25, y: 2.75, z: 3.5, color: 0x111111, scale: 0.5 });
    voxels.push({ x: -0.75, y: 3.0, z: 3.5, color: 0xffffff, scale: 0.25 });
    voxels.push({ x: 0.25, y: 3.0, z: 3.5, color: 0xffffff, scale: 0.25 });

    // Cute rosy cheek blush
    voxels.push({ x: -1.25, y: 2.0, z: 3.25, color: 0xffa0b0, scale: 0.5 });
    voxels.push({ x: 0.75, y: 2.0, z: 3.25, color: 0xffa0b0, scale: 0.5 });

    // 7. Drooping Floppy Sheep Ears
    addMiniBox(voxels, -2.25, 2.25, 2.0, 2, 1, 2, faceCol);
    addMiniBox(voxels, -2.25, 1.75, 2.5, 1, 1, 1, faceCol);
    addMiniBox(voxels, 1.25, 2.25, 2.0, 2, 1, 2, faceCol);
    addMiniBox(voxels, 1.75, 1.75, 2.5, 1, 1, 1, faceCol);

    // 8. Forehead Wool Crown Puff (Cute woolly hair cloud on top!)
    addMiniBox(voxels, -1.25, 3.25, 2.0, 5, 2, 3, woolCol);
    addMiniBox(voxels, -0.75, 4.25, 2.25, 3, 1, 2, woolHighlight);

    assets.push({
      id: `pet_sheep_${style.id}`,
      name: `Sheep - ${style.label}`,
      category: 'prop',
      description: `A puffy cloud-like fluffy sheep with volumetric wool tufts, floppy ears, and a cute woolly crown. Breed style: ${style.label}.`,
      voxels,
      dimensions: { x: 3.5, y: 3.5, z: 4.5 }
    });
  });

  return assets;
}
